import { getUTMParameters } from './cookieUtils';
import { trackEventInNetcore } from './netcore';

export interface TrackingProperties {
  action: string;
  page?: string;
  category?: string;
  cta_name?: string;
  emailid?: string;
  phone_number?: number;
  form_type?: string;
  payment_status?: string;
  product_service_type?: string;
  sku?: string;
  plan_name?: string;
  coupon?: string;
  amount_excl_gst?: number;
  custom_details?: Record<string, unknown>;
  [key: string]: unknown;
}

class EventTracker {
  private basePayload: Record<string, unknown>;

  constructor(private baseProperties: Partial<TrackingProperties>) {
    this.basePayload = {
      ay: new Date().getFullYear(),
      device: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
      source: 'cleartax-web',
      env: process.env.NODE_ENV || 'production',
      timestamp: new Date().toISOString(),
      ...baseProperties,
    };
  }

  trackEvent(action: string, eventProps: Partial<TrackingProperties> = {}) {
    const actionMap: Record<
      string,
      (props: Partial<TrackingProperties>) => void
    > = {
      pageview: this.trackPageview.bind(this),
      click: this.trackClick.bind(this),
      email_requested_calculator_details:
        this.trackEmailRequestedCalculatorDetails.bind(this),
      form_submitted: this.trackFormSubmitted.bind(this),
    };
    if (!actionMap[action]) {
      return;
    }

    let userEmail;
    try {
      userEmail = localStorage.getItem('userEmail');
    } catch {
      userEmail = undefined;
    }
    const mergedProps = {
      emailid: userEmail ?? undefined,
      ...eventProps,
    };

    this.basePayload.timestamp = new Date().toISOString();

    actionMap[action](mergedProps);
  }

  private trackPageview(eventProps: Partial<TrackingProperties>) {
    const utmParams = getUTMParameters();
    const payload = {
      ...this.basePayload,
      action: 'pageview',
      page:
        eventProps.page ||
        this.baseProperties.page ||
        (typeof window !== 'undefined' ? window.location.href : ''),
      category:
        eventProps.category || this.baseProperties.category || 'uncategorized',
      ...utmParams,
      ...eventProps,
    };

    this.sendToTrackingSystem('pageview', payload);
  }

  private trackClick(eventProps: Partial<TrackingProperties>) {
    const payload = {
      ...this.basePayload,
      action: 'click',
      page:
        eventProps.page ||
        this.baseProperties.page ||
        (typeof window !== 'undefined' ? window.location.href : ''),
      category:
        eventProps.category || this.baseProperties.category || 'uncategorized',
      cta_name: eventProps.cta_name || this.baseProperties.cta_name,
      ...eventProps,
    };

    this.sendToTrackingSystem('click', payload);
  }

  private trackEmailRequestedCalculatorDetails(
    eventProps: Partial<TrackingProperties>
  ) {
    const payload = {
      ...this.basePayload,
      action: 'email_requested_calculator_details',
      emailid: eventProps.emailid || this.baseProperties.emailid,
      phone_number: eventProps.phone_number || this.baseProperties.phone_number,
      page:
        eventProps.page ||
        this.baseProperties.page ||
        (typeof window !== 'undefined' ? window.location.href : ''),
      category:
        eventProps.category || this.baseProperties.category || 'uncategorized',
      ...eventProps,
    };

    this.sendToTrackingSystem('email_requested_calculator_details', payload);
  }

  private trackFormSubmitted(eventProps: Partial<TrackingProperties>) {
    const payload = {
      ...this.basePayload,
      action: 'form_submitted',
      form_type: eventProps.form_type || this.baseProperties.form_type,
      emailid: eventProps.emailid || this.baseProperties.emailid,
      phone_number: eventProps.phone_number || this.baseProperties.phone_number,
      page:
        eventProps.page ||
        this.baseProperties.page ||
        (typeof window !== 'undefined' ? window.location.href : ''),
      category:
        eventProps.category || this.baseProperties.category || 'uncategorized',
      ...eventProps,
    };
    this.sendToTrackingSystem('form_submitted', payload);
  }

  private sendToTrackingSystem(
    eventName: string,
    payload: Record<string, unknown>
  ) {
    trackEventInNetcore(eventName, payload);
  }
}

export default EventTracker;
